/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { Fragment, type ReactNode, useCallback, useMemo } from 'react';

import { jsx } from '@compiled/react';

import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import { componentWithFG } from '@atlaskit/platform-feature-flags-react';
import { BFFConsumerEnum, BFFProvider } from '@atlassian/navigation-bff-data-provider';
import { UniversalCreateBFFProvider } from '@atlassian/navigation-bff-data-provider/universal-create';
import { NavigationUFOSegment } from '@atlassian/navigation-perf-utils';
import { ConditionalRelayEnvironmentProvider } from '@atlassian/relay-environment-provider';

import { MenuStateContext } from '../../../context';
import { GenericErrorBoundary } from '../../../services';
import type {
	MenuStateContextProps,
	MixedMenuItems,
	UniversalCreateButtonCombinedProps,
	UniversalCreateButtonProps,
	UniversalCreateButtonPropsV2,
} from '../../../types';
import { useNormalizeItems } from '../../../utils';
import AnalyticsWrapper from '../../components/AnalyticsWrapper';
import { GicModalProvider } from '../../components/CrossAppItem/primitives/GicModalContext';
import { EntryPointPopup } from '../../components/EntryPointPopup';
import { LoadingMenu } from '../../components/LoadingMenu';
import { TriggerButton } from '../../components/TriggerButton';
import { useIsResponsiveView } from '../../utils';

import { popupContentEntryPoint } from './entrypoint';
import { useJsChunkLoadTrack } from './hooks/use-js-chunk-load-track';

/**
 * Type guard to check if props are v1 (has 'items' property)
 */
function isV1Props(props: UniversalCreateButtonCombinedProps): props is UniversalCreateButtonProps {
	return 'items' in props;
}

/**
 * Type guard to check if props are v2 (has 'sections' property)
 */
function isV2Props(
	props: UniversalCreateButtonCombinedProps,
): props is UniversalCreateButtonPropsV2 {
	return 'sections' in props;
}

function UniversalCreateButtonInner(props: UniversalCreateButtonCombinedProps) {
	const {
		children,
		testId,
		onClick,
		onClose,
		enableTriggerPulse = false,
		triggerButtonSpotlightTarget,
		isNav4Enabled = true,
		buttonTheme,
		onMouseEnter,
		onMouseLeave,
		shouldRenderToParent,
		primaryActionItem,
	} = props;

	const isResponsive = useIsResponsiveView();

	// Track JS chunk load time - must be called before the lazy chunk loads
	useJsChunkLoadTrack();

	// Normalize items for v1, or use empty array for v2
	const items = isV1Props(props) ? props.items : undefined;
	const structuredItems = useNormalizeItems(items ?? []);

	const handleTriggerButtonClick = useCallback(() => {
		onClick?.();
	}, [onClick]);

	const handleClose = useCallback(() => {
		onClose?.();
	}, [onClose]);

	const handleMouseEnter = useCallback(
		(event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
			onMouseEnter?.(event);
		},
		[onMouseEnter],
	);

	const menuStateContextValue = useMemo((): MenuStateContextProps => {
		const isV2Mode = isV2Props(props);

		return {
			isV2Mode,
			items: fg('navx_uc_context_refactor')
				? structuredItems
				: isV1Props(props)
					? // keep previous code path, would be removed after fg rollout
					((props.items ?? []) as MixedMenuItems)
					: ([] as MixedMenuItems),
			product: props.product ?? 'unknown',
			subProduct: props.subProduct,
			isNav4Enabled,
			sections: isV2Mode ? props.sections : [],
			crossAppCreationConfig: isV2Mode
				? props.crossAppCreationConfig ?? ({ enabled: false } as const)
				: ({ enabled: false } as const),
			suggestionConfig:
				'suggestionConfig' in props
					? props.suggestionConfig ?? { enabled: false }
					: { enabled: false },
			selectedItem: props.selectedItem,
			preventClose: props.preventClose,
			controlledOpen: props.isOpen,
		};
	}, [props, isNav4Enabled, structuredItems]);

	return (
		<MenuStateContext.Provider value={menuStateContextValue}>
			<GicModalProviderWithFG>
				<EntryPointPopup
					placement={
						(isResponsive && isNav4Enabled) || primaryActionItem ? 'bottom-end' : 'bottom-start'
					}
					// eslint-disable-next-line @atlaskit/design-system/use-should-render-to-parent
					shouldRenderToParent={shouldRenderToParent}
					onClose={handleClose}
					trigger={(triggerProps) => (
						<TriggerButton
							isEntryPointEnabled
							triggerProps={triggerProps}
							testId={testId}
							triggerButtonSpotlightTarget={triggerButtonSpotlightTarget}
							enableTriggerPulse={enableTriggerPulse}
							isOpen={triggerProps['aria-expanded']}
							onMouseEnter={handleMouseEnter}
							onMouseLeave={onMouseLeave}
							onClick={handleTriggerButtonClick}
							isNav4Enabled={isNav4Enabled}
							buttonTheme={buttonTheme}
							primaryActionItem={primaryActionItem}
						>
							{children}
						</TriggerButton>
					)}
					entryPoint={popupContentEntryPoint}
					entryPointProps={props}
					fallback={<LoadingMenu padded />}
				/>
			</GicModalProviderWithFG>
		</MenuStateContext.Provider>
	);
}

function UFOWrapper({ children }: { children: ReactNode }) {
	return <NavigationUFOSegment id="universalCreateComponentLoad">{children}</NavigationUFOSegment>;
}

function NoopComponent<T>(props: { children: ReactNode; value?: T | undefined }) {
	return <Fragment>{props.children}</Fragment>;
}

const GicModalProviderWithFG = componentWithFG(
	'navx_enable_gic',
	GicModalProvider,
	NoopComponent,
);

function UniversalCreateButtonLazyInner(props: UniversalCreateButtonCombinedProps) {
	return (
		<UFOWrapper>
			<ConditionalRelayEnvironmentProvider>
				<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
					<UniversalCreateBFFProvider>
						<AnalyticsWrapper>
							<FlagsProvider>
								<UniversalCreateButtonInner {...props} />
							</FlagsProvider>
						</AnalyticsWrapper>
					</UniversalCreateBFFProvider>
				</BFFProvider>
			</ConditionalRelayEnvironmentProvider>
		</UFOWrapper >
	);
}

export function UniversalCreateButtonLazy(props: UniversalCreateButtonCombinedProps) {
	return (
		<GenericErrorBoundary name="universal-create-button-lazy" app={props.product}>
			<UniversalCreateButtonLazyInner {...props} />
		</GenericErrorBoundary>
	);
}
